import {
	Component,
	ElementRef,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EVENTS, FORM_NAMES, FORM_FIELD_TYPES } from '@constants/analytics';
import { Breed } from '@models/Breed';
import { AnalyticsService } from '@services/analytics.service';

@Component({
	selector: 'qe-autosearch',
	templateUrl: './autosearch.component.html',
	styleUrls: ['./autosearch.component.scss'],
})
export class AutoSearchComponent implements OnInit, OnDestroy {
	@HostBinding('class.autosearch') public klassName = true;
	@Input() disabled: boolean = true;
	@Input() options: Breed[] | null = [];
	@Input() formGroup!: FormGroup;
	@Input() inputId!: string | undefined;
	@Input() breed!: number | string |  undefined;
	public toggleMenu: boolean = false;
	public inputValue: string = '';
	public breeds: Breed[] | undefined = [];
	public selectedBreed: string = '';
	public isFocused: boolean = false;
	private isClickedInside: boolean = false;

	constructor(
		private readonly elementRef: ElementRef,
		private readonly gtmService: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.addOutsideClickListener();
		this.breeds = this.options as Breed[];

		if (this.breed) {
			const breedLabel = (this.options as Breed[]).find((item: Breed) => this.breed === item.id);

			// eslint-disable-next-line eqeqeq
			if (breedLabel != null) {
				this.selectedBreed = breedLabel?.name ?? '';
				this.inputValue = breedLabel?.name ?? '';
				this.isFocused = true;
			}
		}
		this.formGroup?.valueChanges.subscribe(val => {
			if (!val.breed) {
				this.closeMenu();
			}
		});
	}

	ngOnDestroy(): void {
		this.removeOutsideClickListener();
	}

	// this function gets used on value chnages as well as the click handler in the UI
	// if touch is true, we need to update the form values
	// if touch is false, we only update the local component state
	public closeMenu(touch: boolean = false): void {
		this.selectedBreed = '';
		this.inputValue = '';
		this.toggleMenu = false;
		this.isFocused = false;
		this.breeds = this.options as Breed[];

		if (touch) {
			this.formGroup.patchValue({ breed: '' });
			this.formGroup.markAsTouched();
		}
	}

	public onFocus(): void {
		this.isFocused = true;
		if (this.inputValue.length > 0) {
			this.filterSuggestions();
		} else {
			this.breeds = this.options as Breed[];
		}

		this.toggleMenu = true;
	}

	public onBlur(): void {
		if (!!this.inputValue) {
			this.isFocused = true;
		} else {
			this.isFocused = false;
		}
	}

	public onInputChange(event: Event): void {
		if (event.target instanceof HTMLInputElement) {
			if (event.target.value.length === 0) {
				this.breeds = this.options as Breed[];
				this.inputValue = '';
			} else {
				this.inputValue = event.target.value;
				this.filterSuggestions();
			}
		}
	}

	public selectOption(suggestion: Breed): void {
		this.inputValue = suggestion.name;
		this.selectedBreed = suggestion.name;
		this.toggleMenu = false;
		this.isFocused = true;

		this.formGroup.patchValue({ breed: suggestion.id });

		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: 'breed',
			field_value: this.selectedBreed,
			field_type: FORM_FIELD_TYPES.SELECT,
			field_id: `${FORM_FIELD_TYPES.SELECT}-${FORM_NAMES.PET_INFO}-breed`
		});
	}

	public filterSuggestions(): void {
		if (this.inputValue.length > 0 && this.breeds && this.breeds.length > 0) {
			this.breeds = (this.options as Breed[]).filter((item: Breed) =>
				item.name.toLowerCase().includes(this.inputValue.toLowerCase())
			);
		} else {
			this.breeds = this.options as Breed[];
		}
	}

	public addOutsideClickListener(): void {
		this.onClickOutside = (event: MouseEvent): void => {
			if (!this.isClickInsideComponent(event)) {
				this.toggleMenu = false;
			}
		};

		document.addEventListener('click', this.onClickOutside);
	}

	public removeOutsideClickListener(): void {
		document.removeEventListener('click', this.onClickOutside);
	}

	private isClickInsideComponent(event: MouseEvent): boolean {
		const element = event.target as HTMLElement;

		if (this.isClickedInside && this.inputValue === '') {
			this.breeds = this.options as Breed[];
			this.isClickedInside = false;
			this.toggleMenu = true;
		} else {
			this.isClickedInside = true;
		}

		return this.elementRef.nativeElement.contains(element);
	}

	private onClickOutside: (event: MouseEvent) => void = () => null;
}
